export type ServiceRequest = {
    id: string;
    subject: string;
    category: string;
    priority: "High" | "Medium" | "Low";
    status: "Created" | "In Progress" | "Resolved" | "Rejected";
    assignedTo: string;
    createdAt: string;
};

export type PollingStation = {
    id: number;
    partNumber: number;
    name: string;
    requestCount: number;
    coordinates: [number, number][];
    requests: ServiceRequest[];
};

export type Locality = {
    id: number;
    name: string;
    requestCount: number;
    coordinates: [number, number][];
    pollingStations: PollingStation[];
};

export type Constituency = {
    id: number;
    name: string;
    number: number;
    district: string;
    requestCount: number;
    coordinates: [number, number][];
    localities: Locality[];
};

export type Project = {
    id: string;
    name: string;
    constituency: Constituency;
};

/* -------------------------------------------------------
 * REQUEST MOCK DATA
 * ----------------------------------------------------- */

const CATEGORY_TARGETS = [
    { category: "Water", count: 96 },
    { category: "Roads", count: 82 },
    { category: "Electricity", count: 71 },
    { category: "Sanitation", count: 64 },
    { category: "Health", count: 48 },
    { category: "Education", count: 37 },
    { category: "Other", count: 30 },
];

const STATUS_TARGETS: Array<{
    status: ServiceRequest["status"];
    count: number;
}> = [
    { status: "Created", count: 142 },
    { status: "In Progress", count: 86 },
    { status: "Resolved", count: 100 },
    { status: "Rejected", count: 100 },
];

const SUBJECTS: Record<string, string[]> = {
    Water: [
        "Drinking water supply issue",
        "Water pipeline leakage",
        "No water supply",
        "Request for new water connection",
        "Water tank requirement",
        "Low water pressure",
        "Borewell repair required",
    ],

    Roads: [
        "Road damage near main road",
        "Pothole repair required",
        "Road resurfacing request",
        "Internal road damaged",
        "Request for road construction",
        "Road drainage issue",
    ],

    Electricity: [
        "Street light not working",
        "Electric pole damaged",
        "New street light required",
        "Power supply issue",
        "Transformer problem",
    ],

    Sanitation: [
        "Garbage collection issue",
        "Drainage cleaning required",
        "Public toilet maintenance",
        "Waste dumping complaint",
        "Sewage overflow complaint",
    ],

    Health: [
        "Health camp request",
        "PHC facility issue",
        "Medicine availability complaint",
        "Ambulance requirement",
        "Health centre infrastructure issue",
    ],

    Education: [
        "School infrastructure issue",
        "School toilet requirement",
        "Classroom repair request",
        "Teacher requirement",
        "School drinking water issue",
    ],

    Other: [
        "Community hall request",
        "Public facility requirement",
        "Government office issue",
        "General civic complaint",
        "Other public service request",
    ],
};

const ASSIGNED_USERS = ["Ramesh Kumar", "Shankar", "Mahesh", "Ravi", "Kiran", "Suresh", "Kumar", "Prakash"];

const PRIORITIES: ServiceRequest["priority"][] = ["High", "Medium", "Low"];

const buildPool = <T>(targets: Array<{ value: T; count: number }>): T[] => {
    const pool: T[] = [];

    targets.forEach(({ value, count }) => {
        for (let i = 0; i < count; i += 1) {
            pool.push(value);
        }
    });

    return pool;
};

const categoryPool = buildPool(
    CATEGORY_TARGETS.map((item) => ({
        value: item.category,
        count: item.count,
    }))
);

const statusPool = buildPool(
    STATUS_TARGETS.map((item) => ({
        value: item.status,
        count: item.count,
    }))
);

let requestIndex = 0;

const createRequests = (count: number): ServiceRequest[] => {
    return Array.from({ length: count }, () => {
        const index = requestIndex;

        const category = categoryPool[index];
        const status = statusPool[index];
        const subjects = SUBJECTS[category];

        requestIndex += 1;

        return {
            id: `SR-${String(index + 1001).padStart(4, "0")}`,
            subject: subjects[index % subjects.length],
            category,
            priority: PRIORITIES[index % PRIORITIES.length],
            status,
            assignedTo: ASSIGNED_USERS[index % ASSIGNED_USERS.length],
            createdAt: `${String(1 + (index % 18)).padStart(2, "0")} Aug 2026`,
        };
    });
};

/* -------------------------------------------------------
 * POLLING STATION
 * ----------------------------------------------------- */

const createPollingStation = (
    id: number,
    partNumber: number,
    name: string,
    requestCount: number,
    coordinates: [number, number][]
): PollingStation => {
    const requests = createRequests(requestCount);

    return {
        id,
        partNumber,
        name,
        requestCount: requests.length,
        coordinates,
        requests,
    };
};

/* -------------------------------------------------------
 * LOCALITY
 * ----------------------------------------------------- */

const createLocality = (
    id: number,
    name: string,
    coordinates: [number, number][],
    pollingStations: PollingStation[]
): Locality => ({
    id,
    name,
    coordinates,
    pollingStations,
    requestCount: pollingStations.reduce((total, station) => total + station.requests.length, 0),
});

/* -------------------------------------------------------
 * SOUTH TURA LOCALITIES
 *
 * Coordinates are approximate development/mock
 * coordinates around Tura, Meghalaya.
 *
 * They are intentionally NOT represented as exact
 * constituency boundaries.
 * ----------------------------------------------------- */

/*
 * Total requests = 428
 *
 * Tura Town / Central = 128
 *   Upper Babupara       = 32
 *   Police Compound      = 32
 *   Hawakhana            = 32
 *   Chandmary            = 32
 *
 * Akonggre / East = 96
 *   Akonggre             = 32
 *   Fancy Valley         = 32
 *   Tetengkol            = 32
 *
 * Dobasipara / West = 96
 *   Dobasipara           = 32
 *   Rishipara            = 32
 *   Dakopgre             = 32
 *
 * South Tura / Outer = 108
 *   Sampalgre            = 36
 *   Nikwatgre            = 36
 *   Walbakgre            = 36
 *
 * TOTAL = 428
 */

/* -------------------------------------------------------
 * LOCALITY 1
 * ----------------------------------------------------- */

const turaTown = createLocality(
    1,
    "Tura Town",
    [
        [25.5145, 90.2035],
        [25.5205, 90.2115],
        [25.5125, 90.2195],
        [25.5025, 90.211],
        [25.5055, 90.2],
    ],
    [
        createPollingStation(1, 1, "Upper Babupara", 32, [
            [25.5165, 90.2025],
            [25.519, 90.205],
            [25.517, 90.208],
            [25.514, 90.2055],
        ]),

        createPollingStation(2, 2, "Police Compound", 32, [
            [25.518, 90.2085],
            [25.521, 90.211],
            [25.519, 90.214],
            [25.516, 90.2115],
        ]),

        createPollingStation(3, 3, "Hawakhana", 32, [
            [25.511, 90.212],
            [25.514, 90.215],
            [25.512, 90.218],
            [25.5085, 90.215],
        ]),

        createPollingStation(4, 4, "Chandmary", 32, [
            [25.5045, 90.203],
            [25.508, 90.206],
            [25.506, 90.209],
            [25.5025, 90.206],
        ]),
    ]
);

/* -------------------------------------------------------
 * LOCALITY 2
 * ----------------------------------------------------- */

const akonggre = createLocality(
    2,
    "Akonggre",
    [
        [25.525, 90.215],
        [25.534, 90.221],
        [25.531, 90.232],
        [25.521, 90.228],
        [25.518, 90.218],
    ],
    [
        createPollingStation(5, 5, "Akonggre", 32, [
            [25.525, 90.216],
            [25.529, 90.219],
            [25.527, 90.223],
            [25.523, 90.22],
        ]),

        createPollingStation(6, 6, "Fancy Valley", 32, [
            [25.53, 90.222],
            [25.534, 90.226],
            [25.532, 90.23],
            [25.528, 90.227],
        ]),

        createPollingStation(7, 7, "Tetengkol", 32, [
            [25.523, 90.225],
            [25.527, 90.229],
            [25.525, 90.233],
            [25.521, 90.229],
        ]),
    ]
);

/* -------------------------------------------------------
 * LOCALITY 3
 * ----------------------------------------------------- */

const dobasipara = createLocality(
    3,
    "Dobasipara",
    [
        [25.5, 90.19],
        [25.509, 90.195],
        [25.505, 90.205],
        [25.496, 90.202],
        [25.494, 90.193],
    ],
    [
        createPollingStation(8, 8, "Dobasipara", 32, [
            [25.501, 90.191],
            [25.505, 90.194],
            [25.503, 90.198],
            [25.499, 90.195],
        ]),

        createPollingStation(9, 9, "Rishipara", 32, [
            [25.5, 90.198],
            [25.504, 90.201],
            [25.502, 90.205],
            [25.498, 90.202],
        ]),

        createPollingStation(10, 10, "Dakopgre", 32, [
            [25.496, 90.194],
            [25.5, 90.197],
            [25.498, 90.201],
            [25.494, 90.198],
        ]),
    ]
);

/* -------------------------------------------------------
 * LOCALITY 4
 * ----------------------------------------------------- */

const southTuraOuter = createLocality(
    4,
    "South Tura",
    [
        [25.488, 90.202],
        [25.498, 90.209],
        [25.494, 90.221],
        [25.482, 90.217],
        [25.48, 90.207],
    ],
    [
        createPollingStation(11, 11, "Sampalgre", 36, [
            [25.488, 90.203],
            [25.492, 90.206],
            [25.49, 90.21],
            [25.486, 90.207],
        ]),

        createPollingStation(12, 12, "Nikwatgre", 36, [
            [25.488, 90.211],
            [25.492, 90.214],
            [25.49, 90.218],
            [25.486, 90.215],
        ]),

        createPollingStation(13, 13, "Walbakgre", 36, [
            [25.484, 90.21],
            [25.488, 90.213],
            [25.486, 90.217],
            [25.482, 90.214],
        ]),
    ]
);

/* -------------------------------------------------------
 * SOUTH TURA CONSTITUENCY
 * ----------------------------------------------------- */

const constituency: Constituency = {
    id: 1,

    name: "South Tura",

    number: 51,

    district: "West Garo Hills",

    coordinates: [
        [25.535, 90.185],
        [25.545, 90.205],
        [25.535, 90.235],
        [25.515, 90.25],
        [25.49, 90.24],
        [25.475, 90.22],
        [25.475, 90.195],
        [25.495, 90.175],
        [25.52, 90.178],
    ],

    localities: [turaTown, akonggre, dobasipara, southTuraOuter],

    requestCount: [turaTown, akonggre, dobasipara, southTuraOuter].reduce(
        (total, locality) => total + locality.requestCount,
        0
    ),
};

/* -------------------------------------------------------
 * PROJECT
 * ----------------------------------------------------- */

export const mockProject: Project = {
    id: "985",

    name: "South Tura Development Project",

    constituency,
};

/* -------------------------------------------------------
 * DEVELOPMENT VALIDATION
 * ----------------------------------------------------- */

const allPollingStations = constituency.localities.flatMap((locality) => locality.pollingStations);

const allRequests = allPollingStations.flatMap((station) => station.requests);

/* Total validation */

if (constituency.requestCount !== 428) {
    console.warn(`[SR MOCK] Expected 428 constituency requests, got ${constituency.requestCount}`);
}

if (allRequests.length !== 428) {
    console.warn(`[SR MOCK] Expected 428 request records, got ${allRequests.length}`);
}

/* Category validation */

const actualCategoryCounts = allRequests.reduce<Record<string, number>>((result, request) => {
    result[request.category] = (result[request.category] || 0) + 1;

    return result;
}, {});

/* Status validation */

const actualStatusCounts = allRequests.reduce<Record<string, number>>((result, request) => {
    result[request.status] = (result[request.status] || 0) + 1;

    return result;
}, {});

/* Geographic validation */

console.log("[SR MOCK] Total requests:", allRequests.length);

console.log("[SR MOCK] Category counts:", actualCategoryCounts);

console.log("[SR MOCK] Status counts:", actualStatusCounts);

console.log(
    "[SR MOCK] Geographic counts:",
    constituency.localities.map((locality) => ({
        locality: locality.name,

        count: locality.requestCount,

        pollingStations: locality.pollingStations.map((station) => ({
            partNumber: station.partNumber,

            pollingStation: station.name,

            count: station.requestCount,
        })),
    }))
);
